
interface FeatureGroup {
  title: string;
  features: Feature[];
}

interface Feature {
  id: string;
  name: string;
  description: string;
  defaultEnabled: boolean;
}

interface Step3FeaturesProps {
  selectedFeatures: string[];
  onChange: (features: string[]) => void;
}

const FEATURE_GROUPS: FeatureGroup[] = [
  {
    title: 'Payment Processing',
    features: [
      {
        id: 'payments',
        name: 'Credit Card Processing',
        description: 'Accept Visa, Mastercard, Amex',
        defaultEnabled: true,
      },
      {
        id: 'ach_transfers',
        name: 'ACH/Bank Transfers',
        description: 'Direct bank account transfers',
        defaultEnabled: true,
      },
      {
        id: 'digital_wallets',
        name: 'Digital Wallets',
        description: 'Apple Pay, Google Pay support',
        defaultEnabled: true,
      },
    ],
  },
  {
    title: 'Fraud & Security',
    features: [
      {
        id: 'fraud_detection',
        name: 'Fraud Detection',
        description: 'Real-time fraud screening',
        defaultEnabled: true,
      },
      {
        id: '3d_secure',
        name: '3D Secure Authentication',
        description: 'Enhanced payment security',
        defaultEnabled: false,
      },
    ],
  },
  {
    title: 'Payouts & Settlements',
    features: [
      {
        id: 'payouts',
        name: 'Automated Payouts',
        description: 'Schedule and manage payouts',
        defaultEnabled: true,
      },
      {
        id: 'multi_currency',
        name: 'Multi-Currency Support',
        description: 'Accept 150+ currencies',
        defaultEnabled: false,
      },
    ],
  },
];

export function Step3Features({ selectedFeatures, onChange }: Step3FeaturesProps) {
  const toggleFeature = (featureId: string) => {
    if (selectedFeatures.includes(featureId)) {
      onChange(selectedFeatures.filter((id) => id !== featureId));
    } else {
      onChange([...selectedFeatures, featureId]);
    }
  };

  const toggleGroupFeatures = (group: FeatureGroup, enable: boolean) => {
    const groupFeatureIds = group.features.map((f) => f.id);

    if (enable) {
      // Add all group features
      const newFeatures = new Set([...selectedFeatures, ...groupFeatureIds]);
      onChange(Array.from(newFeatures));
    } else {
      // Remove all group features
      onChange(selectedFeatures.filter((id) => !groupFeatureIds.includes(id)));
    }
  };

  const isGroupFullyEnabled = (group: FeatureGroup) => {
    return group.features.every((feature) => selectedFeatures.includes(feature.id));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Select Platform Features</h2>
        <p className="mt-2 text-sm text-gray-600">
          Choose which features to enable for your platform. You can modify these later.
        </p>
      </div>

      <div className="space-y-6">
        {FEATURE_GROUPS.map((group) => (
          <div key={group.title}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">{group.title}</h3>
              <button
                type="button"
                onClick={() => toggleGroupFeatures(group, !isGroupFullyEnabled(group))}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {isGroupFullyEnabled(group) ? 'Disable All' : 'Enable All'}
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {group.features.map((feature, index) => (
                <div
                  key={feature.id}
                  className={`p-4 ${
                    index !== group.features.length - 1 ? 'border-b border-gray-200' : ''
                  }`}
                >
                  <label className="flex items-start gap-3 cursor-pointer group">
                    {/* Checkbox */}
                    <div className="mt-0.5">
                      <input
                        type="checkbox"
                        checked={selectedFeatures.includes(feature.id)}
                        onChange={() => toggleFeature(feature.id)}
                        className="w-[18px] h-[18px] rounded border-2 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                      />
                    </div>

                    {/* Feature info */}
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 group-hover:text-gray-700">
                        {feature.name}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {feature.description}
                      </div>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg
            className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm text-blue-900">
            <strong>Selected: {selectedFeatures.length} features</strong>
            <p className="mt-1 text-blue-800">
              You can enable or disable features at any time from the platform settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
